import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { $api } from '../../api/queryClient'
import type { AgentPoolBridge } from '../../api/schema'
import { useLocale } from '../../contexts/LocaleContext'
import { useAdminSettingPatch, useAdminSettingsRegistry } from '../../hooks/use-admin-settings'
import { notify } from '../../lib/notifications'
import { SECTION_ICONS } from '../../lib/section-icons'
import { formatRelative } from '../activity/ActivityEntry'
import { PageHeader } from '../shared/PageHeader'
import { Badge } from '../ui/badge'
import { Input } from '../ui/input'
import { Skeleton } from '../ui/skeleton'
import { EmptyState } from '../ui/empty-state'

const POLL_MS = 10_000
const MAX_MISSIONS = 'ai.agent.max_concurrent_missions'

function CapacityCard() {
    const { t } = useTranslation('admin')
    const { data } = useAdminSettingsRegistry('setting')
    const patch = useAdminSettingPatch()
    const entry = data?.data.find((e) => e.code === MAX_MISSIONS)
    const current = entry ? String(entry.server_value ?? entry.default_value ?? '') : ''
    const [value, setValue] = useState(current)
    useEffect(() => setValue(current), [current])
    // Shown once the code is registered (spec 2026-10-21-23).
    if (!entry) return null

    const save = () => {
        const n = Number(value)
        if (!Number.isInteger(n) || n < 0 || String(n) === current) {
            setValue(current)
            return
        }
        patch.mutate(
            { params: { path: { code: MAX_MISSIONS } }, body: { server_value: n } },
            {
                onSuccess: () => notify({ key: 'agent_pool_saved', variant: 'default', titleKey: 'admin:agent_pool.saved' }),
                onError: () =>
                    notify({ key: 'agent_pool_error', variant: 'destructive', titleKey: 'admin:agent_pool.save_failed', timeoutMs: 0 }),
            },
        )
    }

    return (
        <section className="rounded-lg border border-border bg-card p-4 space-y-2" data-testid="agent-pool-capacity">
            <h2 className="text-base font-semibold text-foreground">{t('agent_pool.capacity')}</h2>
            <label className="grid gap-2 text-sm sm:grid-cols-[1fr_10rem] sm:items-center">
                <span className="text-muted-foreground">{t('agent_pool.max_missions')}</span>
                <Input
                    type="number"
                    min={0}
                    step={1}
                    value={value}
                    disabled={patch.isPending}
                    onChange={(e) => setValue(e.target.value)}
                    onBlur={save}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') save()
                    }}
                    data-testid="agent-pool-max-missions"
                />
            </label>
        </section>
    )
}

function BridgeRow({ bridge }: { bridge: AgentPoolBridge }) {
    const { t } = useTranslation('admin')
    const { locale } = useLocale()
    return (
        <tr className="border-t border-border align-top" data-testid={`agent-pool-row-${bridge.name}`}>
            <td className="py-2 pr-3 whitespace-nowrap font-medium text-foreground">{bridge.name}</td>
            <td className="py-2 pr-3 whitespace-nowrap font-mono text-xs">{bridge.version}</td>
            <td className="py-2 pr-3">
                <div className="flex flex-wrap gap-1">
                    {(bridge.harnesses ?? []).map((h) => (
                        <Badge key={h.code} variant="secondary" data-testid={`agent-pool-harness-${bridge.name}-${h.code}`}>
                            {h.code}@{h.version}
                        </Badge>
                    ))}
                </div>
            </td>
            <td className="py-2 pr-3">
                <Badge
                    variant={bridge.healthy ? 'success' : 'destructive'}
                    title={bridge.healthy ? undefined : t('agent_pool.unhealthy_hint')}
                    data-testid={`agent-pool-health-${bridge.name}`}
                >
                    {bridge.healthy ? t('agent_pool.healthy') : t('agent_pool.unhealthy')}
                </Badge>
                {!bridge.healthy && <div className="mt-1 text-xs text-destructive">{t('agent_pool.unhealthy_hint')}</div>}
            </td>
            <td className="py-2 pr-3" data-testid={`agent-pool-run-${bridge.name}`}>
                {bridge.run_uid ? (
                    <Link
                        to="/$org/agent-runs/$runUid"
                        params={{ org: bridge.organization_code, runUid: bridge.run_uid }}
                        className="text-primary hover:underline"
                    >
                        {bridge.organization_code} · {bridge.run_uid.slice(0, 8)}
                    </Link>
                ) : (
                    <span className="text-muted-foreground">{t('agent_pool.idle')}</span>
                )}
            </td>
            <td className="py-2 pr-3 font-mono text-xs">{bridge.remote_addr}</td>
            <td className="py-2 pr-3 whitespace-nowrap" title={new Date(bridge.connected_at).toLocaleString(locale)}>
                {formatRelative(bridge.connected_at, locale)}
            </td>
            <td className="py-2 whitespace-nowrap" title={new Date(bridge.last_seen_at).toLocaleString(locale)}>
                {formatRelative(bridge.last_seen_at, locale)}
            </td>
        </tr>
    )
}

/**
 * The agent pool as one backend pod sees it (spec 2026-10-21-19 §7): its
 * bridges, versions, harnesses, current run and health, polled every 10 s.
 * `unhealthy` is shown, never written: an operator deletes the pod.
 */
export function AgentPoolPage(_: { org: string }) {
    const { t } = useTranslation('admin')
    const { data, isLoading } = $api.useQuery('get', '/api/v1/admin/agent-pool', {}, { refetchInterval: POLL_MS })
    const bridges = data?.bridges ?? []
    const cols = ['name', 'version', 'harnesses', 'health', 'run', 'remote', 'connected', 'last_seen']

    return (
        <div className="space-y-6" data-testid="agent-pool-page">
            <PageHeader icon={SECTION_ICONS.adminAgentPool} title={t('agent_pool.title')} description={t('agent_pool.subtitle')} />

            <p className="text-sm text-muted-foreground" data-testid="agent-pool-pod">
                {t('agent_pool.pod', { pod: data?.pod ?? '…' })} {t('agent_pool.pod_note')}
            </p>

            {isLoading ? (
                <Skeleton className="h-24 w-full" />
            ) : bridges.length === 0 ? (
                <EmptyState variant="panel" title={t('agent_pool.empty')} data-testid="agent-pool-empty" />
            ) : (
                <div className="overflow-x-auto rounded-lg border border-border bg-card p-4">
                    <table className="w-full text-sm">
                        <thead className="text-left text-xs text-muted-foreground">
                            <tr>{cols.map((c) => <th key={c} className="py-1 pr-3 font-medium whitespace-nowrap">{t(`agent_pool.cols.${c}`)}</th>)}</tr>
                        </thead>
                        <tbody>
                            {bridges.map((b) => <BridgeRow key={b.connection_id} bridge={b} />)}
                        </tbody>
                    </table>
                </div>
            )}

            <CapacityCard />
        </div>
    )
}
